import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { ArrowLeft, Mail, Phone, Building2 } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import {
  apiGetSupplierProfile,
  type SupplierProfile as SupplierProfileData,
  type SupplierProfileCampaign,
} from '@/lib/api'
import { Button } from '../ui/button'
import { Skeleton } from '../ui/skeleton'
import { EmptyState } from '../EmptyState'
import { SupplierStatusBadgeV2 } from './SupplierStatusBadgeV2'
import { CampaignHistoryTable } from './CampaignHistoryTable'
import { SupplierComplementRequest } from './SupplierComplementRequest'
import { formatDateMedium } from '@/lib/formatDate'
import { cn } from '@/lib/utils'

type OpenCampaignOptions = { campaignSupplierId?: string }

type Props = {
  supplierId: string
  onBack: () => void
  // Story 3.6 — la 2e signature permet de propager `campaignSupplierId`
  // (déjà disponible dans `SupplierProfileCampaign`) pour ouvrir directement
  // la vue campagne × fournisseur. Optionnel pour rester testable.
  onOpenCampaign?: (campaignId: string, options?: OpenCampaignOptions) => void
}

type LoadError = 'not-found' | 'generic' | null

export function SupplierProfile({ supplierId, onBack, onOpenCampaign }: Props) {
  const { logout } = useAuth()
  const [data, setData] = useState<SupplierProfileData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<LoadError>(null)
  const [complementTarget, setComplementTarget] = useState<SupplierProfileCampaign | null>(null)

  // P3 : séquenceur pour ignorer les réponses des requêtes obsolètes
  // (changement rapide de supplierId ou clic répété sur Réessayer).
  const requestSeqRef = useRef(0)

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    const seq = ++requestSeqRef.current
    try {
      const res = await apiGetSupplierProfile(supplierId)
      if (seq !== requestSeqRef.current) return
      setData(res)
    } catch (e: unknown) {
      if (seq !== requestSeqRef.current) return
      const msg = e instanceof Error ? e.message : 'Erreur lors du chargement du profil'
      // Pattern Adv P5 Story 2.2 — détection stricte pour éviter faux-positifs
      // (ex: message contenant juste "401" dans un texte).
      if (/^API error 401\b/.test(msg)) {
        logout()
        return
      }
      // 404 = ressource absente, 422 = id malformé côté client — même UX
      // (profil introuvable) plutôt que de laisser l'utilisateur avec un toast
      // d'erreur technique.
      if (/^API error 404\b/.test(msg) || /^API error 422\b/.test(msg)) {
        setError('not-found')
        return
      }
      setError('generic')
      toast.error(msg)
    } finally {
      if (seq === requestSeqRef.current) {
        setIsLoading(false)
      }
    }
  }, [supplierId, logout])

  useEffect(() => {
    load()
  }, [load])

  // Escape → retour à la liste. On n'attache le listener qu'une fois monté.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      // Si un composant descendant (modal, popover, combobox) a déjà traité
      // Escape, ne pas doubler l'action.
      if (e.defaultPrevented) return
      const target = e.target as HTMLElement | null
      const tag = target?.tagName
      const isEditable = target?.isContentEditable ?? false
      // Laisser les champs texte gérer Escape (vider/blur) — ne pas capturer.
      if (tag === 'INPUT' || tag === 'TEXTAREA' || isEditable) return
      onBack()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onBack])

  const showSkeleton = isLoading && data === null

  // En état 404, l'EmptyState porte déjà son propre CTA « Retour à la liste » :
  // on masque le bouton header pour éviter deux boutons homonymes (a11y) et une
  // ambiguïté dans les tests qui cherchent le bouton par son label.
  const showHeaderBack = error !== 'not-found'

  return (
    <div className="space-y-6">
      {showHeaderBack && (
        <div className="sticky top-0 z-10 -mx-4 bg-background/95 px-4 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/75">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-1.5" aria-hidden="true" />
            Retour à la liste
          </Button>
        </div>
      )}

      {showSkeleton ? (
        <div className="space-y-4">
          <Skeleton className="h-8 w-1/3" />
          <Skeleton className="h-5 w-1/2" />
          <div className="mt-6 space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </div>
      ) : error === 'not-found' ? (
        <EmptyState
          icon="❓"
          title="Fournisseur introuvable"
          description="Ce fournisseur n'existe plus ou vous n'y avez pas accès."
          secondaryLabel="Retour à la liste"
          onSecondaryClick={onBack}
        />
      ) : error === 'generic' ? (
        <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center">
          <p className="text-sm text-destructive">Impossible de charger le profil du fournisseur.</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={load}>
            Réessayer
          </Button>
        </div>
      ) : data ? (
        <>
          <ProfileHeader data={data} />
          <CampaignsSection
            data={data}
            onOpenCampaign={onOpenCampaign}
            onRequestComplement={setComplementTarget}
          />
        </>
      ) : null}

      {complementTarget && (
        <SupplierComplementRequest
          supplierId={supplierId}
          campaignSupplierId={complementTarget.campaignSupplierId}
          campaignName={complementTarget.campaignName}
          sections={complementTarget.formTemplateSections ?? []}
          open={complementTarget !== null}
          onOpenChange={(open) => {
            if (!open) setComplementTarget(null)
          }}
          onSuccess={() => {
            load()
          }}
        />
      )}
    </div>
  )
}

function ProfileHeader({ data }: { data: SupplierProfileData }) {
  const { supplier } = data
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {supplier.isOverdue && (
            <span
              className={cn('inline-block h-2 w-2 rounded-full bg-destructive')}
              aria-label="En retard sur ≥1 campagne"
              role="img"
            />
          )}
          <h1 className="text-2xl font-bold truncate">{supplier.name}</h1>
        </div>
        <SupplierStatusBadgeV2 status={supplier.aggregateStatus} size="md" />
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5">
          Client depuis le {formatDateMedium(supplier.createdAt)}
        </span>
        <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5">
          {supplier.campaignCount} campagne{supplier.campaignCount > 1 ? 's' : ''}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <InfoCell icon={<Mail className="h-4 w-4" />} label="Email">
          <a className="text-primary hover:underline" href={`mailto:${supplier.email}`}>
            {supplier.email}
          </a>
        </InfoCell>
        <InfoCell icon={<Building2 className="h-4 w-4" />} label="Entreprise">
          <span>{supplier.company ?? '—'}</span>
        </InfoCell>
        <InfoCell icon={<Phone className="h-4 w-4" />} label="Téléphone">
          {supplier.phone ? (
            <a
              className="text-primary hover:underline"
              // Sanitisation : on ne garde que `+` et chiffres dans le href.
              // Le texte affiché conserve l'original (espaces, tirets, etc.).
              href={`tel:${supplier.phone.replace(/[^+\d]/g, '')}`}
            >
              {supplier.phone}
            </a>
          ) : (
            <span>—</span>
          )}
        </InfoCell>
      </div>

      {supplier.notes ? (
        <div>
          <h3 className="text-sm font-medium mb-1">Notes</h3>
          <p className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-md bg-muted/30 p-3 text-sm text-muted-foreground">
            {supplier.notes}
          </p>
        </div>
      ) : null}
    </div>
  )
}

function InfoCell({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-center gap-2 rounded-md border p-3 text-sm">
      <span className="text-muted-foreground">{icon}</span>
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="truncate">{children}</div>
      </div>
    </div>
  )
}

function CampaignsSection({
  data,
  onOpenCampaign,
  onRequestComplement,
}: {
  data: SupplierProfileData
  onOpenCampaign?: (campaignId: string, options?: OpenCampaignOptions) => void
  onRequestComplement?: (campaign: SupplierProfileCampaign) => void
}) {
  const { campaigns } = data
  return (
    <section aria-labelledby="supplier-history-heading" className="space-y-3">
      <h2 id="supplier-history-heading" className="text-lg font-semibold">
        Historique des campagnes ({campaigns.length})
      </h2>
      {campaigns.length === 0 ? (
        <EmptyState
          icon="📋"
          title="Aucune campagne"
          description="Ce fournisseur n'a pas encore été invité à répondre à une campagne"
          primaryLabel="Inviter à une campagne"
        />
      ) : (
        <CampaignHistoryTable
          campaigns={campaigns}
          onOpenCampaign={onOpenCampaign}
          onRequestComplement={onRequestComplement}
        />
      )}
    </section>
  )
}
